import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  RichTextEditorComponent,
  Inject,
  Toolbar,
  HtmlEditor,
  Image,
  Link,
  QuickToolbar,
} from "@syncfusion/ej2-react-richtexteditor";
import { ActionIcon } from "@mantine/core";
import { IconCheck, IconX, IconPencil } from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import useStartupStore from "../stores/startupStore";
import Mixpanel from "../components/Lib/mixpanel";

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 1rem 0 1rem;
`;

const EditorContainer = styled.div`
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  margin-inline: auto;
  background: rgba(255, 255, 255, 0.9);
`;

const ErrorMessage = styled.div`
  background: #fdecea;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const GuideEditor = () => {
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const editorRef = useRef(null);

  const { program,  slug, content, setContent, selectedProgram, isOwner, isMember, isSubmitted } = useStartupStore(
    (state) => ({
      program: state.program,
      slug: state.slug,
      content: state.content,
      setContent: state.setContent,
      selectedProgram: state.selectedProgram,
      isOwner: state.isOwner,
      isMember: state.isMember,
      isSubmitted: state.isSubmitted
    })
  );

  const pageId = selectedProgram?.cid;
  const index = selectedProgram?.sid;

  // Load guide content stored as HTML
  const [initialContent, setInitialContent] = useState("");
  useEffect(() => {
    if (content[pageId]?.[index]?.guide) {
      setInitialContent(content[pageId][index].guide);
    }
  }, [pageId, index, content]);

  const getCsrfToken = () => {
    const metaTag = document.querySelector("meta[name='csrf-token']");
    return metaTag ? metaTag.content : "";
  };

  // Minimal toolbar configuration
  const minimalToolbar = {
    enable: editMode,
    items: ["Bold", "Italic", "Underline", "CreateLink", "Image"],
  };

  const onImageUpload = (args) => {
    const csrfToken = getCsrfToken();
    args.customHeaders = { "X-CSRF-Token": csrfToken };
  };

  const onImageRemove = (args) => {
    console.log("Image removed:", args);
    // With insertImageSettings.removeUrl set, the editor will send a delete request automatically.
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    const startTime = performance.now();
    const categoryId = program.categories[pageId]?.id;
    const subCategoryId = program.categories[pageId]?.sub_categories[index]?.id;
    console.log("category id is "+ categoryId + "subcategory id is " + subCategoryId);
    // Retrieve HTML content directly from the editor
    const htmlContent = editorRef.current.getHtml();
    try {

      const response = await fetchApi(
        `/sub_categories/${content[pageId]?.[index]?.id}/update_guide`,
        "PATCH",
        { sub_category: { guide: htmlContent } }
      );
      const responseTime = Math.round(performance.now() - startTime);
      if (response.ok) {
        const updatedData = await response.json();
        setContent(null, htmlContent);
        setEditMode(false);
        Mixpanel.content('Guide Content', 'Updated', {
          sub_category_id: subCategoryId,
          category_id: categoryId,
          startup_slug: slug,
          new_content_length: htmlContent.length,
          response_time_ms: responseTime
      });
      } else {
        const error = await response.json();
        setSaveError(error.message || "Failed to save changes");
        Mixpanel.error('Guide Content Save Error', {
          error_message: error.message,
          error_type: 'API Error',
          component: 'GuideEditor',
          user_action: 'Save Guide',
          sub_category_id: subCategoryId,
          category_id: categoryId,
          startup_slug: slug,
          http_status: response.status,
          response_time_ms: responseTime
        });
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error during guide update:", error);
      Mixpanel.error('Guide Content Save Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'GuideEditor',
        user_action: 'Save Guide',
        sub_category_id: subCategoryId,
        category_id: categoryId,
        startup_slug: slug,
        response_time_ms: responseTime
      });
      setSaveError("An unexpected error occurred while saving");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {!!saveError && (
        <ErrorMessage>
          <IconX size={16} />
          {saveError}
        </ErrorMessage>
      )}
      <EditorContainer>
          <ActionButtons>
            <h4 style={{ margin: 0 }}>Guide</h4>
            { !isSubmitted && (
              (isOwner || isMember) && editMode ? (
                <>
                  <ActionIcon onClick={() => setEditMode(false)} variant="subtle">
                    <IconX size={18} />
                  </ActionIcon>
                  <ActionIcon
                    onClick={handleSave}
                    variant="subtle"
                    disabled={isSaving}
                  >
                    <IconCheck size={18} />
                  </ActionIcon>
                </>
              ) : (
                <ActionIcon
                  variant="subtle"
                  onClick={() => setEditMode(true)}
                  style={{ marginLeft: "auto" }}
                >
                  <IconPencil size={18} />
                </ActionIcon>
              )
            )}
          </ActionButtons>

        <RichTextEditorComponent
          ref={editorRef}
          readonly={!editMode}
          value={initialContent}
          toolbarSettings={minimalToolbar}
          fileUploading={onImageUpload}
          removed={onImageRemove}
          readOnly={!editMode}
          height={isCollapsed ? 150 : 400}
        >
          <Inject services={[Toolbar, HtmlEditor, Image, Link, QuickToolbar]} />
        </RichTextEditorComponent>
        <CollapseButton onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? "See more" : "See less"}
      </CollapseButton>
      </EditorContainer>
     
    </>
  );
};

export default GuideEditor;